/**
 * AppointmentFormPage — criar/editar compromisso, no desenho oficial de
 * `/quotes/new`.
 *
 * Era o `AppointmentFormModal`. Virou ROTA (`/agenda/novo`, `/agenda/:id/editar`)
 * pelos mesmos motivos do #163/#164: o desenho oficial é layout de PÁGINA, o
 * formulário deixa de disputar altura com o `max-h-[90vh]` do diálogo e o botão
 * físico de voltar passa a funcionar sozinho.
 *
 * COMO O CONTEXTO VIAJA: o dia escolhido no calendário vai na URL (`?data=`), e
 * não no state da navegação — é um dado simples, e assim ele sobrevive a
 * recarregar a página. Nada aqui é de autorização.
 *
 * O VALOR AO VIVO é o TÍTULO, e não um número: compromisso não tem dinheiro
 * (#160/#195). A sublinha é quando/onde — que é o que o usuário confere de
 * relance ao marcar uma visita.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  ClipboardList,
  Loader2,
  MapPin,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ClientSelector } from '@/components/quotes/ClientSelector';
import { useServiceOrders } from '@/hooks/useServiceOrders';
import {
  useAppointments,
  useCreateAppointment,
  useUpdateAppointment,
  useDeleteAppointment,
  APPOINTMENT_STATUSES,
  REMINDER_OPTIONS,
  type AppointmentInput,
} from '@/hooks/useAppointments';
import { ensureReminderPermission } from '@/lib/appointmentReminders';
import { todayIsoDate } from '@/lib/financeStatus';
import { localDateFromIsoDatePart } from '@/lib/calendarEvents';
import { cn } from '@/lib/utils';
import type { AppointmentStatus, Client } from '@/types/database';

/** Cor do pontinho de cada status na pílula do hero. */
const STATUS_DOT: Record<AppointmentStatus, string> = {
  scheduled: 'bg-blue-400',
  done: 'bg-emerald-400',
  cancelled: 'bg-red-400',
};

/** Divide um `starts_at` ISO em data/hora LOCAIS para os inputs. */
function parseStartsAt(
  iso: string | null | undefined,
  allDay: boolean,
): { dateStr: string; timeStr: string } {
  if (!iso) return { dateStr: todayIsoDate(), timeStr: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { dateStr: todayIsoDate(), timeStr: '' };
  const dateStr = todayIsoDate(d);
  if (allDay) return { dateStr, timeStr: '' };
  const timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return { dateStr, timeStr };
}

/** Monta o instante LOCAL a partir dos inputs (ver a nota de fuso em useAppointments). */
function buildStartsAt(dateStr: string, timeStr: string, allDay: boolean): string {
  const [y, mo, dd] = dateStr.split('-').map(Number);
  if (allDay || !timeStr) {
    return new Date(y, (mo || 1) - 1, dd || 1, 0, 0, 0, 0).toISOString();
  }
  const [hh, mm] = timeStr.split(':').map(Number);
  return new Date(y, (mo || 1) - 1, dd || 1, hh || 0, mm || 0, 0, 0).toISOString();
}

export default function AppointmentFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const defaultDate = searchParams.get('data');

  /** Fechar = voltar para de onde veio (a agenda, no dia que estava aberto). */
  const close = () => navigate(-1);

  // A entidade em edição vem da LISTA já cacheada (offline-first).
  const { appointments, isLoading: loadingAppointments } = useAppointments();
  const appointment = id ? (appointments.find((a: any) => a.id === id) ?? null) : null;
  const isEditing = Boolean(appointment);

  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  const deleteAppointment = useDeleteAppointment();

  const { orders } = useServiceOrders();

  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showOrderPicker, setShowOrderPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [dateStr, setDateStr] = useState(defaultDate || todayIsoDate());
  const [timeStr, setTimeStr] = useState('09:00');
  const [allDay, setAllDay] = useState(false);
  const [status, setStatus] = useState<AppointmentStatus>('scheduled');
  const [reminderMinutes, setReminderMinutes] = useState<number | null>(null);
  const [serviceOrderId, setServiceOrderId] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);

  /**
   * Hidrata UMA vez. No diálogo o gatilho era `open` (e o dado já existia);
   * aqui a página nasce montada e o compromisso chega depois — sem o marcador,
   * cada refetch da lista reescreveria o que está sendo digitado.
   */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (id && !appointment) return;
    hydratedRef.current = true;
    if (appointment) {
      const isAllDay = appointment.all_day === true;
      const { dateStr: d, timeStr: t } = parseStartsAt(appointment.starts_at, isAllDay);
      setClientId(appointment.client_id ?? null);
      setClientName(appointment.client_name ?? '');
      setTitle(appointment.title ?? '');
      setDescription(appointment.description ?? '');
      setLocation(appointment.location ?? '');
      setDateStr(d);
      setTimeStr(t || '09:00');
      setAllDay(isAllDay);
      setStatus((appointment.status ?? 'scheduled') as AppointmentStatus);
      setReminderMinutes(appointment.reminder_minutes ?? null);
      setServiceOrderId(appointment.service_order_id ?? null);
      setQuoteId(appointment.quote_id ?? null);
      return;
    }
    setDateStr(defaultDate || todayIsoDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appointment]);

  const linkedOrder = useMemo(
    () => (serviceOrderId ? (orders.find((o: any) => o.id === serviceOrderId) ?? null) : null),
    [orders, serviceOrderId],
  );

  const handleSelectClient = (client: Client) => {
    setClientId(client.id);
    setClientName(client.name ?? '');
  };

  const linkOrder = (order: any) => {
    setServiceOrderId(order.id);
    setQuoteId(order.quote_id ?? null);
    // Preenche a partir da OS o que ainda estiver vazio.
    if (!clientName.trim()) {
      setClientId(order.client_id ?? null);
      setClientName(order.client_name ?? '');
    }
    if (!title.trim()) {
      setTitle(order.title || order.client_name || 'Ordem de serviço');
    }
    setShowOrderPicker(false);
  };

  const handleReminderChange = (value: string) => {
    const minutes = value === 'none' ? null : Number(value);
    setReminderMinutes(minutes);
    // Pede a permissão no momento em que o usuário opta (nativo, best-effort).
    if (minutes != null) {
      void ensureReminderPermission();
    }
  };

  const handleSubmit = async () => {
    if (!dateStr) {
      toast.warning('Escolha uma data para o compromisso.');
      return;
    }
    if (!title.trim() && !clientName.trim() && !serviceOrderId) {
      toast.warning('Informe um título, um cliente ou vincule a uma ordem de serviço.');
      return;
    }

    const payload: AppointmentInput = {
      client_id: clientId,
      client_name: clientName.trim() || null,
      service_order_id: serviceOrderId,
      quote_id: quoteId,
      title: title.trim() || null,
      description: description.trim() || null,
      location: location.trim() || null,
      starts_at: buildStartsAt(dateStr, timeStr, allDay),
      ends_at: null,
      all_day: allDay,
      status,
      reminder_minutes: reminderMinutes,
    };

    try {
      if (isEditing && appointment) {
        await updateAppointment.mutateAsync({ id: appointment.id, updates: payload });
        toast.success('Compromisso atualizado.');
      } else {
        const res = await createAppointment.mutateAsync(payload);
        toast.success(
          (res as any)?.offline ? 'Compromisso salvo localmente.' : 'Compromisso criado.',
        );
      }
      close();
    } catch {
      // toast tratado no onError da mutation
    }
  };

  const handleDelete = async () => {
    if (!appointment?.id) return;
    try {
      await deleteAppointment.mutateAsync(appointment.id);
      toast.success('Compromisso excluído.');
      close();
    } catch {
      // toast tratado no onError da mutation
    }
  };

  const saving =
    createAppointment.isPending || updateAppointment.isPending || deleteAppointment.isPending;

  /* ==================================================================== */
  /* HERO — o mesmo cabeçalho sangrado de /quotes/new.                     */
  /* -------------------------------------------------------------------- */
  /* Valor ao vivo = TÍTULO (não há dinheiro aqui). A sublinha diz QUANDO e */
  /* ONDE. As pílulas de status ocupam o lugar das do orçamento: são três   */
  /* opções e cabem numa grade de 3 colunas.                               */
  const heroDate = dateStr ? localDateFromIsoDatePart(dateStr) : null;
  const heroWhen = heroDate
    ? `${format(heroDate, "EEE, d 'de' MMM", { locale: ptBR })}${allDay ? ' · dia inteiro' : timeStr ? ` · ${timeStr}` : ''}`
    : '';
  const heroSubline = [heroWhen, location.trim() || clientName.trim()].filter(Boolean).join(' · ');

  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={close}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {isEditing ? 'Editando' : 'Novo compromisso'}
          </p>
          <h1 className="truncate text-lg font-bold leading-tight">Agenda</h1>
        </div>
        {/* Excluir vira ÍCONE no cabeçalho, como nas outras telas de edição
            (#160) — e ele precisa de `aria-label`, senão o botão fica sem nome
            acessível (#142). */}
        {isEditing && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
            onClick={() => setConfirmDelete(true)}
            disabled={saving}
            aria-label="Excluir compromisso"
            title="Excluir compromisso"
          >
            <Trash2 className="h-4.5 w-4.5" />
          </Button>
        )}
      </div>

      {/* Título ao vivo — `line-clamp-2` e não `truncate`: é texto livre, e o
          que distingue um compromisso do outro não pode ser cortado (#160). */}
      <div className="relative mt-5">
        <p className="text-xs text-white/75">Compromisso</p>
        <p className="line-clamp-2 break-words text-2xl font-bold leading-tight">
          {title.trim() || clientName.trim() || 'Sem título'}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">
          {heroSubline || 'Preencha os dados abaixo'}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-1.5">
        {APPOINTMENT_STATUSES.map((option) => {
          const active = option.value === status;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatus(option.value)}
              aria-pressed={active}
              className={cn(
                'flex min-w-0 items-center justify-center gap-1.5 rounded-full px-2 py-1.5 text-xs font-medium transition-all',
                active
                  ? 'bg-white text-emerald-800 shadow-[inset_0_-3px_0_rgba(0,0,0,0.12)]'
                  : 'bg-white/15 text-white/85 hover:bg-white/25',
              )}
            >
              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[option.value])} />
              <span className="truncate">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Edição de um compromisso que ainda não chegou (cache frio, link direto).
  if (id && !appointment && loadingAppointments) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          {hero}
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (id && !appointment) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h1 className="text-2xl font-bold text-muted-foreground">Compromisso não encontrado</h1>
          <Button3D className="mt-6" onClick={() => navigate('/agenda')}>
            Ver agenda
          </Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* ── O QUE E QUANDO ──────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="appt-title">Título</FieldLabel>
              <Input
                id="appt-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Visita técnica, reunião, orçamento presencial"
                className={FIELD_INPUT}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel htmlFor="appt-date">Data</FieldLabel>
                <label
                  htmlFor="appt-all-day"
                  className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground"
                >
                  Dia inteiro
                  <Switch id="appt-all-day" checked={allDay} onCheckedChange={setAllDay} />
                </label>
              </div>
              <div className="grid grid-cols-2 items-start gap-3">
                <Input
                  id="appt-date"
                  type="date"
                  value={dateStr}
                  onChange={(e) => setDateStr(e.target.value)}
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
                <Input
                  type="time"
                  value={timeStr}
                  onChange={(e) => setTimeStr(e.target.value)}
                  disabled={allDay}
                  aria-label="Horário"
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="appt-reminder">Lembrete</FieldLabel>
              <Select
                value={reminderMinutes == null ? 'none' : String(reminderMinutes)}
                onValueChange={handleReminderChange}
              >
                <SelectTrigger id="appt-reminder" className={FIELD_INPUT}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REMINDER_OPTIONS.map((r) => (
                    <SelectItem key={r.label} value={r.value == null ? 'none' : String(r.value)}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── COM QUEM E ONDE ─────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="appt-client" icon={User}>
                Cliente
              </FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="appt-client"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Nome do cliente (opcional)"
                  className={cn(FIELD_INPUT, 'min-w-0 flex-1')}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  onClick={() => setShowClientSelector(true)}
                  aria-label="Escolher um cliente cadastrado"
                  title="Escolher um cliente cadastrado"
                >
                  <User className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="appt-location" icon={MapPin}>
                Local
              </FieldLabel>
              <Input
                id="appt-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Endereço ou referência (opcional)"
                className={FIELD_INPUT}
              />
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="appt-notes">Observações</FieldLabel>
              <Textarea
                id="appt-notes"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detalhes do compromisso"
                rows={2}
              />
            </div>
          </div>

          {/* ── VÍNCULO COM A ORDEM DE SERVIÇO (carrega o orçamento junto) ─ */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <FieldLabel icon={ClipboardList}>Vincular a ordem de serviço</FieldLabel>
            {linkedOrder ? (
              <div className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 break-words text-sm">
                    {linkedOrder.title || linkedOrder.client_name || 'Ordem de serviço'}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => {
                    setServiceOrderId(null);
                    setQuoteId(null);
                  }}
                  aria-label="Desvincular a ordem de serviço"
                  title="Desvincular a ordem de serviço"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start rounded-xl"
                onClick={() => setShowOrderPicker(true)}
              >
                <ClipboardList className="mr-2 h-4 w-4" />
                Selecionar ordem de serviço
              </Button>
            )}
          </div>
        </div>

        {/* Barra FLUTUANTE — filha DIRETA do container da página. */}
        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <div className="flex gap-2">
            <Button3D color="gray" size="sm" className="flex-1" onClick={close} disabled={saving}>
              Cancelar
            </Button3D>
            <Button3D size="sm" className="flex-1" onClick={handleSubmit} disabled={saving}>
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {isEditing ? 'Salvar' : 'Criar'}
            </Button3D>
          </div>
        </div>
      </div>

      <ClientSelector
        open={showClientSelector}
        onOpenChange={setShowClientSelector}
        onSelect={handleSelectClient}
      />

      <OrderPickerDialog
        open={showOrderPicker}
        onOpenChange={setShowOrderPicker}
        orders={orders}
        onPick={linkOrder}
      />

      {/* `window.confirm` não existe na WebView com o mesmo comportamento em
          todo lugar, e o diálogo do app é o padrão das outras exclusões. */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este compromisso?</AlertDialogTitle>
            <AlertDialogDescription>
              Ele sai da agenda e o lembrete local é cancelado. Não dá para desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* Cancelar NUNCA é desabilitado junto com o confirmar (#41). */}
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}

/**
 * Picker da ordem de serviço a vincular. Lê as OS já cacheadas (offline-first),
 * mais recentes primeiro. Continua sendo DIÁLOGO: é escolha pontual dentro do
 * formulário, e virar rota poria o rascunho no histórico entre a pergunta e a
 * resposta.
 */
function OrderPickerDialog({
  open,
  onOpenChange,
  orders,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orders: any[];
  onPick: (order: any) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Selecione uma ordem de serviço</DialogTitle>
        </DialogHeader>
        {orders.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma ordem de serviço encontrada
          </p>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onPick(o)}
                className="w-full rounded-md border p-3 text-left transition hover:bg-muted"
              >
                <p className="font-medium">{o.title || o.client_name || 'Ordem de serviço'}</p>
                {o.client_name && o.title && (
                  <p className="text-xs text-muted-foreground">{o.client_name}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
